<template>
  <!-- Vue2 强制要求：唯一根元素 -->
  <div class="navbar-root">
    <div class="navbar-container" :class="{ collapsed: isCollapsed }">
      <!-- Logo区域 -->
      <div class="navbar-logo" @click="toggleCollapse">
        <div class="logo-content">
          <img v-if="!isCollapsed" src="@/assets/images/logo-full.png" alt="Logo" class="logo-full">
          <img v-else src="@/assets/images/logo-mini.png" alt="Logo" class="logo-mini">
          <span v-if="!isCollapsed" class="logo-text">管理系统</span>
        </div>
        <div class="collapse-btn" @click.stop="toggleCollapse">
          <i :class="isCollapsed ? 'el-icon-s-unfold' : 'el-icon-s-fold'"></i>
        </div>
      </div>

      <!-- 关键修复：v-if/v-else 是该父容器仅有的两个子元素，无任何干扰 -->
      <div class="menu-wrapper">
        <!-- 菜单区域：仅保留基础v-if，去掉内部复杂逻辑 -->
        <div class="navbar-menu" ref="navbarMenu" v-if="!loading">
          <div
              v-for="(item, index) in getListData"
              :key="index"
          class="menu-item"
          :class="{ active: activeMenu === index + '', showSubmenu: item.children && item.children.length > 0 }"
          :data-index="index"
          @click="handleMenuSelect(index + '')"
          @mouseenter="handleMenuHover(item)"
          >
          <div class="menu-icon">
            <i :class="item.icon ? item.icon : 'el-icon-menu'"></i>
          </div>
          <div class="menu-text" v-if="!isCollapsed">
            {{ item.name ? item.name : '未命名菜单' }}
          </div>
          <div class="menu-arrow" v-if="!isCollapsed && item.children && item.children.length > 0">
            <i class="el-icon-arrow-right"></i>
          </div>
        </div>
        <div v-if="getListData.length === 0" class="empty-menu">暂无菜单权限</div>
      </div>
      <!-- 加载中：v-else 与上面的v-if直接相邻，无任何间隔 -->
      <div class="menu-loading" v-else>
        <i class="el-icon-loading"></i> 菜单加载中...
      </div>
    </div>

    <!-- 弹出式子菜单 -->
    <transition name="submenu-fade">
      <div
          v-if="showSubmenu && activeSubmenu"
          class="submenu-panel"
          :class="{ collapsed: isCollapsed }"
          :style="{ top: submenuTop + 'px' }"
          @mouseenter="handleSubmenuEnter"
          @mouseleave="handleSubmenuLeave"
          ref="submenuPanel"
      >
        <div class="submenu-header">
          <h3>{{ activeSubmenu.name ? activeSubmenu.name : '子菜单' }}</h3>
          <div class="submenu-close" @click="closeSubmenu">
            <i class="el-icon-close"></i>
          </div>
        </div>

        <div class="submenu-content">
          <div class="submenu-list">
            <!-- template v-for：key移到内部，且只用index+名称做key -->
            <template v-for="(subItem, subIndex) in activeSubmenu.children">
              <div
                  v-if="subItem.children && subItem.children.length > 0"
                  :key="'sub-' + subIndex"
                  class="second-menu-item has-children"
                  @mouseenter="() => handleSecondMenuHover(subItem)"
                  @mouseleave="handleSecondMenuLeave"
              >
                <div class="second-menu-title" @click.stop="handleSecondMenuClick(subItem)">
                  <i :class="subItem.icon ? subItem.icon : 'el-icon-menu'"></i>
                  <span>{{ subItem.name ? subItem.name : '未命名子菜单' }}</span>
                  <i class="el-icon-arrow-right arrow-icon"></i>
                </div>

                <transition name="third-menu-fade">
                  <div
                      v-show="activeThirdMenu === subIndex + ''"
                      class="third-menu-panel"
                  >
                    <div
                        v-for="(thirdItem, thirdIndex) in subItem.children"
                        :key="'third-' + thirdIndex"
                        class="third-menu-item"
                        @click.stop="handleThirdMenuClick(thirdItem)"
                    >
                      <i :class="thirdItem.icon ? thirdItem.icon : 'el-icon-menu'"></i>
                      <span>{{ thirdItem.name ? thirdItem.name : '未命名三级菜单' }}</span>
                    </div>
                  </div>
                </transition>
              </div>

              <div
                  v-else
                  :key="'sub-normal-' + subIndex"
                  class="second-menu-item"
                  @click.stop="handleSecondMenuClick(subItem)"
              >
                <i :class="subItem.icon ? subItem.icon : 'el-icon-menu'"></i>
                <span>{{ subItem.name ? subItem.name : '未命名子菜单' }}</span>
              </div>
            </template>
          </div>
        </div>
      </div>
    </transition>
  </div>
  </div>
</template>

<script>
export default {
  name: "NavbarView",
  data() {
    return {
      isCollapsed: false,
      activeMenu: '',
      activeSubmenu: null,
      activeThirdMenu: null,
      showSubmenu: false,
      submenuTop: 0,
      getListData: [],
      hoverTimer: null,
      leaveTimer: null,
      clickMode: false,
      loading: true
    }
  },
  watch: {
    isCollapsed(newVal) {
      if (newVal) this.closeSubmenu();
      this.$emit('collapse-change', newVal);
      localStorage.setItem('menuCollapsed', newVal);
      if (this.showSubmenu && this.activeSubmenu) {
        this.$nextTick(() => this.calculateSubmenuPosition());
      }
    }
  },
  created() {
    const savedCollapsed = localStorage.getItem('menuCollapsed');
    this.isCollapsed = savedCollapsed !== null ? JSON.parse(savedCollapsed) : false;
    this.menuData();
  },
  mounted() {
    this.setDefaultActiveMenu();
    document.addEventListener('click', this.handleDocumentClick);
  },
  beforeDestroy() {
    clearTimeout(this.hoverTimer);
    clearTimeout(this.leaveTimer);
    this.hoverTimer = null;
    this.leaveTimer = null;
    document.removeEventListener('click', this.handleDocumentClick);
  },
  methods: {
    async menuData() {
      this.loading = true;
      try {
        const param = {
          company_id: sessionStorage.getItem('company_id') || '',
          access_token: sessionStorage.getItem('token') || '',
          uid: sessionStorage.getItem('uid') || '',
          role_id: JSON.parse(sessionStorage.getItem('roles') || '[]')
              .map(role => role.id)
              .filter(Boolean)
              .join(',')
        };
        const res = await this.$axios.proxyModuleList(param);
        if (res.code === 200) {
          this.getListData = res.data || [];
          if (this.getListData.length > 0 && !this.activeMenu) {
            this.activeMenu = '0'; // 初始激活第一个菜单（index=0）
          }
        }
      } catch (error) {
        console.error('获取菜单数据失败:', error);
        this.getListData = [];
      } finally {
        this.loading = false;
      }
    },
    handleMenuSelect(index) {
      if (this.loading || this.getListData.length === 0) return;
      this.clickMode = true;
      clearTimeout(this.hoverTimer);

      // 按index查找菜单（简化：不用id，只用索引）
      const menuItem = this.getListData[Number(index)];
      if (menuItem) {
        if (menuItem.children && menuItem.children.length > 0) {
          this.activeMenu = index;
          this.activeSubmenu = menuItem;
          this.showSubmenu = true;
          this.calculateSubmenuPosition();
        } else if (menuItem.path) {
          const path = menuItem.path.startsWith('/') ? menuItem.path : '/' + menuItem.path;
          this.$router.push(path).catch(err => console.error('路由跳转失败:', err));
          this.closeSubmenu();
        } else {
          this.closeSubmenu();
        }
      } else {
        this.closeSubmenu();
      }

      setTimeout(() => this.clickMode = false, 100);
    },
    handleMenuHover(item) {
      if (this.clickMode || this.isCollapsed || this.loading) return;
      clearTimeout(this.leaveTimer);
      clearTimeout(this.hoverTimer);

      this.hoverTimer = setTimeout(() => {
        if (item && item.children && item.children.length > 0) {
          // 按item找index（简化匹配）
          const index = this.getListData.findIndex(i => i === item);
          this.activeMenu = index + '';
          this.activeSubmenu = item;
          this.showSubmenu = true;
          this.calculateSubmenuPosition();
        }
      }, 200);
    },
    calculateSubmenuPosition() {
      const activeElement = document.querySelector('.menu-item.active');
      if (!activeElement || !this.$el) return;

      const rect = activeElement.getBoundingClientRect();
      const navbarRect = this.$el.getBoundingClientRect();
      let top = rect.top - navbarRect.top;
      const submenuHeight = this.activeSubmenu?.children ? this.activeSubmenu.children.length * 44 + 50 : 300;
      const windowHeight = window.innerHeight;

      if (top + submenuHeight > windowHeight && windowHeight > submenuHeight + 20) {
        top = windowHeight - submenuHeight - 20;
      }
      this.submenuTop = Math.max(top, 60);
    },
    handleSubmenuEnter() {
      clearTimeout(this.leaveTimer);
    },
    handleSubmenuLeave() {
      if (this.isCollapsed) return;
      clearTimeout(this.leaveTimer);

      this.leaveTimer = setTimeout(() => {
        const isHover = document.querySelector('.submenu-panel:hover, .second-menu-item:hover, .third-menu-panel:hover');
        if (!isHover) this.closeSubmenu();
      }, 300);
    },
    handleSecondMenuHover(subItem) {
      if (subItem && subItem.children && subItem.children.length > 0) {
        // 按subItem找index（简化）
        const index = this.activeSubmenu.children.findIndex(i => i === subItem);
        this.activeThirdMenu = index + '';
      }
    },
    handleSecondMenuLeave() {
      clearTimeout(this.leaveTimer);
      setTimeout(() => {
        const isHover = document.querySelector('.third-menu-panel:hover, .second-menu-item.has-children:hover');
        if (!isHover) this.activeThirdMenu = null;
      }, 100);
    },
    handleSecondMenuClick(subItem) {
      if (!subItem) return;
      if (!subItem.children || subItem.children.length === 0) {
        if (subItem.path) {
          const path = subItem.path.startsWith('/') ? subItem.path : '/' + subItem.path;
          this.$router.push(path).catch(err => console.error('路由跳转失败:', err));
          this.closeSubmenu();
        }
      } else {
        const index = this.activeSubmenu.children.findIndex(i => i === subItem);
        const currentId = index + '';
        this.activeThirdMenu = this.activeThirdMenu === currentId ? null : currentId;
      }
    },
    handleThirdMenuClick(thirdItem) {
      if (!thirdItem || !thirdItem.path) return;
      const path = thirdItem.path.startsWith('/') ? thirdItem.path : '/' + thirdItem.path;
      this.$router.push(path).catch(err => console.error('路由跳转失败:', err));
      this.closeSubmenu();
    },
    closeSubmenu() {
      this.showSubmenu = false;
      this.activeThirdMenu = null;
      clearTimeout(this.hoverTimer);
      clearTimeout(this.leaveTimer);
    },
    toggleCollapse() {
      this.isCollapsed = !this.isCollapsed;
    },
    setDefaultActiveMenu() {
      const currentPath = this.$route.path;
      const findActiveMenu = (menuList) => {
        for (let i = 0; i < menuList.length; i++) {
          const item = menuList[i];
          if (item.path && currentPath.includes(item.path)) {
            return i + '';
          }
          if (item.children && item.children.length > 0) {
            const childId = findActiveMenu(item.children);
            if (childId) return childId;
          }
        }
        return '';
      };
      const activeId = findActiveMenu(this.getListData);
      if (activeId) this.activeMenu = activeId;
    },
    handleDocumentClick(event) {
      const navbar = this.$el;
      const submenuPanel = this.$refs.submenuPanel;
      if (!navbar || (submenuPanel && submenuPanel.contains(event.target))) return;
      if (!navbar.contains(event.target)) this.closeSubmenu();
    }
  }
}
</script>

<style scoped lang="less">
/* 根容器 */
.navbar-root {
  height: 100%;
  width: 100%;
}

/* 菜单包裹容器：仅包含v-if/v-else两个子元素 */
.menu-wrapper {
  flex: 1;
  display: flex;
  flex-direction: column;
}

.navbar-container {
  width: 200px;
  height: 100%;
  background: linear-gradient(180deg, #304156 0%, #2b3446 100%);
  position: relative;
  display: flex;
  flex-direction: column;
  transition: width 0.3s ease;
  z-index: 1000;

  &.collapsed {
    width: 64px !important;

    .navbar-menu {
      .menu-text,
      .menu-arrow {
        opacity: 0;
        width: 0;
        margin: 0;
      }
    }

    .collapse-btn i {
      transform: rotate(180deg);
    }
  }
}

/* 加载中样式 */
.menu-loading {
  flex: 1;
  display: flex;
  align-items: center;
  justify-content: center;
  color: #bfcbd9;
  font-size: 14px;
  gap: 8px;

  i {
    animation: el-icon-loading 1s linear infinite;
  }
}

/* 空菜单样式 */
.empty-menu {
  padding: 20px;
  text-align: center;
  color: #bfcbd9;
  font-size: 14px;
}

/* Logo区域 */
.navbar-logo {
  height: 60px;
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 0 20px;
  background: #2b2f3a;
  border-bottom: 1px solid #404854;
  cursor: pointer;
  transition: all 0.3s;
  flex-shrink: 0;

  &:hover {
    background: #263445;
  }
}

.logo-content {
  display: flex;
  align-items: center;
  gap: 12px;
}

.logo-full {
  height: 32px;
  transition: all 0.3s;
}

.logo-mini {
  height: 32px;
  width: 32px;
  display: none;
}

.logo-text {
  color: #fff;
  font-size: 18px;
  font-weight: bold;
  white-space: nowrap;
  transition: all 0.3s;
}

.navbar-container.collapsed .logo-content {
  justify-content: center;

  .logo-full {
    display: none;
  }

  .logo-mini {
    display: block;
  }

  .logo-text {
    opacity: 0;
    width: 0;
    margin-left: 0;
  }
}

.navbar-container.collapsed .collapse-btn {
  display: none;
}

.collapse-btn {
  color: #bfcbd9;
  font-size: 18px;
  cursor: pointer;
  padding: 4px;
  border-radius: 4px;
  transition: all 0.3s;

  &:hover {
    color: #fff;
    background: rgba(255, 255, 255, 0.1);
  }

  i {
    transition: transform 0.3s;
  }
}

/* 菜单区域 */
.navbar-menu {
  flex: 1;
  overflow-y: auto;
  padding: 10px 0;
  scrollbar-width: thin;
  scrollbar-color: #4a5568 transparent;
}

.navbar-menu::-webkit-scrollbar {
  width: 6px;
}

.navbar-menu::-webkit-scrollbar-track {
  background: transparent;
}

.navbar-menu::-webkit-scrollbar-thumb {
  background-color: #4a5568;
  border-radius: 3px;
}

.menu-item {
  height: 50px;
  display: flex;
  align-items: center;
  padding: 0 20px;
  color: #bfcbd9;
  cursor: pointer;
  transition: all 0.3s;
  position: relative;
  overflow: hidden;

  &:hover {
    background: #263445;
    color: #fff;

    .menu-arrow {
      opacity: 1;
    }
  }

  &.active {
    background: #409eff;
    color: #fff;

    &::after {
      content: '';
      position: absolute;
      right: 0;
      top: 0;
      bottom: 0;
      width: 3px;
      background: #fff;
    }

    .menu-arrow {
      color: #fff;
    }
  }
}

.menu-icon {
  width: 20px;
  height: 20px;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 18px;
  flex-shrink: 0;
}

.menu-text {
  flex: 1;
  margin-left: 12px;
  font-size: 14px;
  white-space: nowrap;
  opacity: 1;
  transition: all 0.3s;
  text-align: left;
  min-width: 0;
}

.menu-arrow {
  color: #bfcbd9;
  font-size: 12px;
  opacity: 0.5;
  transition: all 0.3s;
  margin-left: 8px;
}

/* 子菜单面板 */
.submenu-panel {
  position: fixed;
  left: 200px;
  top: 60px;
  bottom: auto;
  width: 220px;
  background: #fff;
  box-shadow: 4px 0 16px rgba(0, 0, 0, 0.15);
  z-index: 1001;
  display: flex;
  flex-direction: column;
  overflow: hidden;
  border-left: 1px solid #e9ecef;
  border-radius: 0 8px 8px 0;
  max-height: 80vh;
  overflow-y: auto;

  &.collapsed {
    left: 64px !important;
  }
}

.submenu-fade-enter-active,
.submenu-fade-leave-active {
  transition: all 0.3s ease;
}

.submenu-fade-enter-from,
.submenu-fade-leave-to {
  transform: translateX(-10px);
  opacity: 0;
}

.submenu-header {
  height: 50px;
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 0 20px;
  background: #f8f9fa;
  border-bottom: 1px solid #e9ecef;
  flex-shrink: 0;
  position: sticky;
  top: 0;
  z-index: 1;

  h3 {
    margin: 0;
    font-size: 16px;
    font-weight: 600;
    color: #333;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
  }
}

.submenu-close {
  color: #999;
  cursor: pointer;
  font-size: 16px;
  padding: 4px;
  border-radius: 4px;
  transition: all 0.3s;

  &:hover {
    color: #409eff;
    background: #e8f4ff;
  }
}

.submenu-content {
  flex: 1;
  overflow-y: auto;
  padding: 10px 0;
}

.submenu-list {
  padding: 0;
}

.second-menu-item {
  padding: 0 20px;
  height: 44px;
  display: flex;
  align-items: center;
  color: #333;
  cursor: pointer;
  transition: all 0.3s;
  position: relative;

  &:hover {
    background: #f5f7fa;
    color: #409eff;

    .arrow-icon {
      color: #409eff;
    }
  }

  i:first-child {
    margin-right: 10px;
    font-size: 16px;
    color: #666;
  }

  span {
    flex: 1;
    font-size: 14px;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
  }

  .arrow-icon {
    margin-left: 8px;
    font-size: 12px;
    color: #999;
    transition: all 0.3s;
  }
}

.second-menu-item.has-children {
  position: relative;
}

.third-menu-panel {
  position: absolute;
  left: 100%;
  top: 0;
  width: 200px;
  background: #fff;
  box-shadow: 4px 0 16px rgba(0, 0, 0, 0.15);
  border-left: 1px solid #e9ecef;
  z-index: 1002;
  max-height: 300px;
  overflow-y: auto;
  padding: 8px 0;
  border-radius: 0 8px 8px 0;
}

.third-menu-fade-enter-active,
.third-menu-fade-leave-active {
  transition: all 0.3s ease;
}

.third-menu-fade-enter-from,
.third-menu-fade-leave-to {
  transform: translateX(-10px);
  opacity: 0;
}

.third-menu-item {
  padding: 0 20px;
  height: 40px;
  display: flex;
  align-items: center;
  color: #666;
  cursor: pointer;
  transition: all 0.3s;

  &:hover {
    background: #f5f7fa;
    color: #409eff;
  }

  i {
    margin-right: 10px;
    font-size: 14px;
  }

  span {
    font-size: 13px;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
  }
}

/* 滚动条样式 */
.submenu-content::-webkit-scrollbar,
.third-menu-panel::-webkit-scrollbar {
  width: 6px;
}

.submenu-content::-webkit-scrollbar-track,
.third-menu-panel::-webkit-scrollbar-track {
  background: #f1f1f1;
}

.submenu-content::-webkit-scrollbar-thumb,
.third-menu-panel::-webkit-scrollbar-thumb {
  background: #c1c1c1;
  border-radius: 3px;
}

.submenu-content::-webkit-scrollbar-thumb:hover,
.third-menu-panel::-webkit-scrollbar-thumb:hover {
  background: #a8a8a8;
}

/* 响应式 */
@media screen and (max-width: 992px) {
  .navbar-container {
    position: fixed;
    left: 0;
    top: 0;
    bottom: 0;
    z-index: 1000;

    &.collapsed {
      transform: translateX(-100%);
    }
  }

  .submenu-panel {
    left: 200px;
    top: 0;
    z-index: 1001;

    &.collapsed {
      left: 64px !important;
    }
  }

  .third-menu-panel {
    left: 220px;
  }
}
</style>